import Links from './Links';
import SurveyContainer from '../SurveyContainer';
import colors from '../../colors';
import BannerContainer from '../../Shared/BannerContainer';
import * as React from 'react';
import styled from 'styled-components';
import { Message } from '../../../typedefs/store';
import {
    media,
    styledWithProps,
    subtleScrollbarStyles,
    withMedia,
    WithMediaProps,
} from '../../../util/styles';
import MessageListContainer from '../../Shared/MessageListContainer';
import Base, { BaseProps, BaseState } from '../BaseConversationWrapper';
import IconBar from './IconBar';
import Minimized from './Minimized';
import FullOverlay from '../../Shared/FullOverlay';
import Title from './Title';
import SendForm from './SendForm';
import WarningButton from '../../Shared/WarningButton';
import * as AlertIcon from 'react-icons/lib/io/alert-circled';
import Spinner from '../../Shared/Spinner';
import VideoContainer from '../../Shared/VideoContainer';
const debounce = require('lodash.debounce');

interface State extends BaseState {
    windowHeight: number;
    windowWidth: number;
}
class Mobile extends Base<BaseProps, State> {
    private debouncer: () => void;

    constructor(props: BaseProps) {
        super(props);
        this.state = { windowHeight: window.innerHeight, windowWidth: window.innerWidth };
        this.debouncer = debounce(this.updateDimensions.bind(this), 150);
        this.updateDimensions = this.updateDimensions.bind(this);
    }

    componentDidMount() {
        window.addEventListener('resize', this.debouncer, false);
        window.addEventListener('orientationchange', this.debouncer, false);
        this.updateDimensions();

        // this is a hack because the elements we need for the calculation aren't rendered yet
        setTimeout(this.updateDimensions.bind(this), 1000);
    }

    componentDidUpdate() {
        this.updateDimensions();
        window.removeEventListener('resize', this.debouncer);
    }

    updateDimensions() {
        if (
            this.state.windowHeight !== window.innerHeight ||
            this.state.windowWidth !== window.innerWidth
        ) {
            this.setState({ windowHeight: window.innerHeight, windowWidth: window.innerWidth });
        }
        setTimeout(() => {
            if (
                this.state.windowHeight !== window.innerHeight ||
                this.state.windowWidth !== window.innerWidth
            ) {
                this.setState({ windowHeight: window.innerHeight, windowWidth: window.innerWidth });
            }
        }, 1000);
    }

    public render() {
        if (this.props.isMinimized) {
            return (
                <Minimized
                    numUnread={this.props.numUnread}
                    onClick={this.props.toggleMinimized}
                    settings={this.props.settings}
                />
            );
        }

        return (
            <Positioner
                className="conversations-chat-container conversations-chat-mobile"
                windowWidth={this.state.windowWidth}
                zIndex={this.props.settings.zIndex}
            >
                <IconBar onMinimize={this.props.toggleMinimized} onClose={this.onClose} />
                <Wrapper className="conversations-chat wrapper" windowHeight={this.state.windowHeight}>
                    {!this.props.ended && (
                        <Header className="conversations-header">
                            <Title
                                className="conversations-header-title"
                                locationName={this.props.locationName}
                            />
                        </Header>
                    )}
                    <VideoContainer wrapperComponent={VideoWrapper} />
                    <BannerContainer style={{ margin: '-15px 0 10px 0' }} />
                    <Body className="body" innerRef={c => (this.bodyRef = c)}>
                        {this.props.showEndDialog && this.renderEndPending()}
                        {this.props.ending && this.renderEnding()}
                        <MessageListContainer
                            send={this.handleSend}
                            scrollToBottom={this.scrollToBottom}
                            showAvatars={true}
                        />
                    </Body>
                    {!this.props.ended ? (
                        <SendForm
                            send={this.handleSend}
                            changeTypingStatus={this.props.changeTypingStatus}
                            updateDimensions={this.updateDimensions}
                            disabled={this.props.disableSending}
                        />
                    ) : !this.props.surveyCompleted ? (
                        <SurveyContainer />
                    ) : (
                        <Links links={this.props.links} />
                    )}
                </Wrapper>
            </Positioner>
        );
    }

    private renderEndPending() {
        return (
            <FullOverlayLarger className="conversations-prompt-container conversations-prompt">
                <div className="conversations-prompt-title">
                    Are you sure you want to end this conversation?
                </div>
                <ButtonWrapper className="conversations-prompt-control">
                    <WarningButton
                        className="conversations-prompt-control-cancel"
                        onClick={this.onClose}
                    >Cancel
                    </WarningButton>
                    <WarningButton
                        className="conversations-prompt-control-confirm"
                        onClick={this.endConvo}
                        primary={true}
                    ><AlertIcon style={{ marginRight: 5 }} /> End
                    </WarningButton>
                </ButtonWrapper>
            </FullOverlayLarger>
        );
    }

    private renderEnding() {
        return (
            <FullOverlay className="conversations-window-loading">
                <Spinner className="conversations-window-loading-icon" size={32} />
            </FullOverlay>
        );
    }
}

export const BaseMobile = Mobile;
export default withMedia(Mobile);

const iconBarHeight = 40;

type PositionerProps = {
    windowWidth: number;
    zIndex: string;
};

export const Positioner = styledWithProps<PositionerProps>(styled.div)`
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    background-color: rgba(255, 255, 255, 0.98);
    padding: 0;
    padding-top: ${iconBarHeight}px;
    max-width: ${props => props.windowWidth}px;
    z-index: ${props => props.zIndex};
`;

const Wrapper = styledWithProps<{ windowHeight: number }>(styled.div)`
    display: flex;
    flex-direction: column;
    height: ${props => props.windowHeight - iconBarHeight}px;
`;

const Body = styled.div`
    flex: 1 0;
    margin: 0 10px 10px 10px;
    overflow-y: auto;
    overflow-x: hidden;
    display: flex;
`;

const Header = styled.div`
    margin: -10px 0 10px 0;
    border-bottom: 1px solid ${colors.lightGray};
`;

const FullOverlayLarger = styled(FullOverlay)`
    font-size: 1.5em;
    justify-content: center;
`;

const ButtonWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 1em;
    & button {
        margin-right: 10px;
        font-size: 0.9em;
        &:last-child {
            margin-right: 0;
        }
    }
`;

const VideoWrapper = styled.div`
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
    background-color: #242424;
    top: 0;
    left: 0;
    height: 250px;
    width: 100%;
    color: white;
    box-shadow: 0px 9px 14px -8px rgba(0, 0, 0, 0.75);
`;



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/Conversation/Mobile/MobileWrapper.tsx